import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  employerClaim,
  employerLogin,
  fetchPublicPreview,
  markCandidateListened,
  markPreviewOpened,
  type PublicPreview,
} from "@/lib/employer-api";
import { resetPassword } from "@/lib/candidate-api";
import { MAX_PREVIEW_CANDIDATES } from "@/components/site/employer-crm";
import { JobDetailSkeleton } from "@/components/cl/Skeletons";
import EmployerInviteView from "@/components/cl/views/EmployerInviteView";

export const Route = createFileRoute("/welcome/$ref")({
  head: () => {
    const t = "We've found salespeople for your role | CLOSDD";
    const d =
      "Salespeople have applied for your vacancy on CLOSDD. Hear their 60-second pitches, then create your free employer account to hear everyone who applied.";
    return {
      meta: [
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "noindex" },
      ],
    };
  },
  // ?token= comes from the invitation email: the employer's login already
  // exists, so the form sets its password instead of creating a new account.
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search["token"] === "string" && search["token"] ? { token: search["token"] } : {},
  component: WelcomePage,
});

const BARS = 48;
const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const initialsOf = (s: string) =>
  s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const notice = (text: string, bad = false) => (
  <p
    role="alert"
    style={{ position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", background: "#FFFFFF", color: bad ? "#B3261E" : "#0B2160", border: "1px solid #E3E3DE", borderRadius: 8, padding: "12px 18px", fontFamily: "Archivo, system-ui, sans-serif", fontWeight: 700, zIndex: 50, maxWidth: "90vw" }}
  >
    {text}
  </p>
);

function WelcomePage() {
  const { ref } = Route.useParams();
  const [preview, setPreview] = useState<PublicPreview | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchPublicPreview(ref).then((found) => {
      if (cancelled) return;
      setPreview(found);
      setChecked(true);
      if (found) markPreviewOpened(ref);
    });
    return () => {
      cancelled = true;
    };
  }, [ref]);

  if (!checked) return <JobDetailSkeleton />;

  if (!preview) {
    return (
      <div style={{ minHeight: "100vh", background: "#FFFFFF", color: "#0B2160", fontFamily: "Archivo, system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
        <div style={{ maxWidth: 560 }}>
          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.03em" }}>This link is not active.</h1>
          <p style={{ margin: "12px 0 0", fontSize: 18, lineHeight: 1.5, color: "#3A3A40" }}>
            It may have already been used to create an account, or expired. Email info@closdd.com and we will help you.
          </p>
        </div>
      </div>
    );
  }

  return <Invite preview={preview} refId={ref} />;
}

function Invite({ preview, refId }: { preview: PublicPreview; refId: string }) {
  const { token } = Route.useSearch();
  const { job, employer } = preview;
  const people = useMemo(
    () =>
      preview.candidates.slice(0, MAX_PREVIEW_CANDIDATES).map((c) => {
        const name = c.lastInitial ? `${c.firstName} ${c.lastInitial}.` : c.firstName;
        return {
          id: c.id,
          audioUrl: c.audioUrl,
          initials: initialsOf(name),
          name,
          role: "Salesperson",
          loc: c.location || "UK",
          years: c.years || "Experience on profile",
          headline: c.years ? `${c.years} in sales.` : "Pitch ready to hear.",
          facts: [c.years, c.location].filter(Boolean),
        };
      }),
    [preview],
  );
  const total = people.length;

  const [cur, setCur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [heard, setHeard] = useState<Record<number, boolean>>({});
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const audio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const a = new Audio();
    audio.current = a;
    a.addEventListener("timeupdate", () => setPos(a.currentTime));
    a.addEventListener("loadedmetadata", () => setDur(Number.isFinite(a.duration) ? a.duration : 0));
    a.addEventListener("ended", () => setPlaying(false));
    return () => a.pause();
  }, []);

  const go = (n: number, play: boolean) => {
    const a = audio.current;
    const p = people[n];
    if (!a || !p) return;
    if (n !== cur || a.src === "") {
      a.pause();
      a.src = p.audioUrl ?? "";
      setPos(0);
      setDur(0);
    }
    setCur(n);
    if (play && p.audioUrl) {
      void a.play();
      setPlaying(true);
      setHeard((h) => ({ ...h, [n]: true }));
      markCandidateListened(refId, p.id);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const p = people[cur];
  const lk = !!liked[cur];
  const likedCount = Object.values(liked).filter(Boolean).length;
  const lit = dur ? Math.floor((pos / dur) * BARS) : 0;
  const wv: Record<string, string> = { cls: playing ? "wv-on" : "" };
  for (let k = 0; k < BARS; k++) wv["c" + k] = k < lit ? "#FF5A3C" : "#2A4393";

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const email = (e.currentTarget.querySelector("#iv-email") as HTMLInputElement | null)?.value.trim() ?? "";
    const password = (e.currentTarget.querySelector("#iv-pass") as HTMLInputElement | null)?.value ?? "";
    setError("");
    setBusy(true);
    try {
      if (token) {
        // A token that was already used (they set a password before) just
        // means: log in with the password they now typed.
        await resetPassword(token, password).catch(() => undefined);
        await employerLogin(email, password);
      } else {
        await employerClaim({ ref: refId, contactName: employer.company || job.company, email, phone: "", password });
      }
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const base = "min-height:48px;padding:0 20px;border-radius:4px;font:inherit;font-weight:800;font-size:15px;cursor:pointer;";
  const v = {
    company: job.company || employer.company,
    companyInitials: initialsOf(job.company || employer.company),
    jobTitle: job.title,
    location: job.location || "UK",
    contract: job.contract || "Not stated",
    pay: job.pay || "Agreed directly with you",
    total,
    advertHref: `/jobs/${preview.ref.toLowerCase()}`,
    cur: p
      ? {
          ...p,
          n: cur + 1,
          likedPressed: lk ? "true" : "false",
          likeLabel: lk ? "On your shortlist" : "Add to shortlist",
          likeStyle: base + (lk ? "background:#FFFFFF;color:#0B2160;border:2px solid #FFFFFF" : "background:#FF5A3C;color:#0B2160;border:2px solid #FF5A3C"),
          like: () => setLiked((l) => ({ ...l, [cur]: !lk })),
        }
      : { facts: [] },
    wv,
    queue: people.map((q, k) => {
      const isCur = k === cur;
      const isL = !!liked[k];
      const isH = !!heard[k];
      return {
        n: k + 1,
        initials: q.initials,
        name: q.name,
        role: q.role,
        years: q.years,
        len: q.audioUrl ? (isCur && dur ? fmt(dur) : "1:00") : "No pitch yet",
        current: isCur ? "true" : "false",
        pick: () => go(k, true),
        tag: isL ? "Shortlisted" : isCur && playing ? "Playing" : isH ? "Heard" : "New",
        tagStyle:
          "font-size:12px;font-weight:800;padding:1px 8px;border-radius:999px;" +
          (isL ? "background:#FF5A3C;color:#0B2160" : isCur && playing ? "background:#0B2160;color:#FFFFFF" : isH ? "background:#DDF2E3;color:#1F6B3A" : "background:#EFEFEA;color:#3A3A40"),
        avatarStyle:
          "width:40px;height:40px;flex:none;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;" +
          (isCur ? "background:#FF5A3C;color:#0B2160" : "background:#0B2160;color:#FFFFFF"),
        style:
          "display:flex;gap:12px;align-items:center;width:100%;padding:14px 20px;border:0;font:inherit;color:#0B2160;cursor:pointer;" +
          (isCur ? "background:#EEF1F8" : "background:transparent"),
      };
    }),
    isPlaying: playing,
    isPaused: !playing,
    playAria: `${playing ? "Pause " : "Play "}${p?.name ?? ""}'s pitch`,
    timeLabel: `${fmt(playing || pos ? pos : 0)} / ${dur ? fmt(dur) : "1:00"}`,
    togglePlay: () => go(cur, !playing),
    prev: () => go((cur + total - 1) % total, playing),
    next: () => go((cur + 1) % total, playing),
    heardCount: Object.keys(heard).length,
    likedCount,
    accEyebrow: likedCount ? "Your shortlist" : "Hear every candidate",
    accTitle: likedCount ? `Save your shortlist of ${likedCount} and hear all your candidates.` : "Create your account to hear all your candidates.",
    accCta: busy ? "Creating your account…" : likedCount ? "Save my shortlist" : "Hear all my candidates",
    notSaved: !saved,
    saved,
    save,
  };

  if (!total) {
    return (
      <>
        <EmployerInviteView v={{ ...v, cur: { facts: [] }, queue: [] }} />
        {error ? notice(error, true) : null}
      </>
    );
  }

  return (
    <>
      <EmployerInviteView v={v} />
      {error ? notice(error, true) : null}
    </>
  );
}
